import { useMemo } from 'react';
import { isSameMonth, parseISO } from 'date-fns';
import { LessonSchedule } from '@/constants/Interfaces/CommonInterfaces';

export interface CalendarEvent {
  id: string;
  title: string;
  type: 'group' | 'private' | 'time-off';
  color: string;
  lesson?: any;
  instructor?: any;
  date: string;
}

// Monthly API response structure
interface MonthlyLessonRecord {
  date: string;
  lessonCodes: Array<{
    id: number;
    slug: string;
    code: string;
    description: string;
    color: string;
    is_group_lesson: number;
    service_mode: string;
  }>;
}

export const useCalendarEvents = (
  lessonSchedule: LessonSchedule[],
  currentMonth: Date
) => {
  // Generate events for the month
  const monthEvents = useMemo(() => {
    const events: CalendarEvent[] = [];

    // Check if this is monthly data structure (has date and lessonCodes)
    if (lessonSchedule.length > 0 && 'date' in lessonSchedule[0]) {
      // Handle monthly API response structure
      const monthlyData = lessonSchedule as unknown as MonthlyLessonRecord[];

      monthlyData.forEach(record => {
        const recordDate = parseISO(record.date);

        // Only process events for the current month
        if (isSameMonth(recordDate, currentMonth)) {
          record.lessonCodes.forEach((lessonCode, lessonIndex) => {
            // Determine event type based on lesson code and properties
            let eventType: 'group' | 'private' | 'time-off' = 'private';

            if (lessonCode.is_group_lesson === 1) {
              eventType = 'group';
            } else if (
              lessonCode.code.includes('T-OFF') ||
              lessonCode.code.includes('Time Off')
            ) {
              eventType = 'time-off';
            }

            const color = lessonCode.color || '#3b82f6';

            events.push({
              id: `${record.date}-${lessonCode.id}-${lessonIndex}`,
              title: lessonCode.description,
              type: eventType,
              color,
              lesson: {
                start: record.date,
                end: record.date,
                lesson_code: {
                  code: lessonCode.code,
                  color: lessonCode.color
                }
              },
              instructor: {
                name: 'Scheduled',
                slug: 'scheduled'
              },
              date: record.date
            });
          });
        }
      });
    } else {
      // Handle daily API response structure (existing logic)
      lessonSchedule.forEach(instructor => {
        instructor.lessons?.forEach((lesson, lessonIndex) => {
          const lessonDate = new Date(lesson.start);
          if (isSameMonth(lessonDate, currentMonth)) {
            // Determine event type based on lesson code
            let eventType: 'group' | 'private' | 'time-off' = 'private';
            if (lesson.lesson_code?.code?.includes('Group')) {
              eventType = 'group';
            } else if (
              lesson.lesson_code?.code?.includes('Time Off') ||
              lesson.lesson_code?.code?.includes('T-OFF')
            ) {
              eventType = 'time-off';
            }

            const color = lesson.lesson_code?.color || '#3b82f6';

            events.push({
              id: `${instructor.slug}-${lesson.start}-${lessonIndex}`,
              title: `${instructor.name} - ${lesson.lesson_code?.code || 'Lesson'}`,
              type: eventType,
              color,
              lesson,
              instructor,
              date: lesson.start
            });
          }
        });
      });
    }

    return events;
  }, [lessonSchedule, currentMonth]);

  return monthEvents;
};
